import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiSliders,
  FiX,
} from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import "../css/shop.scss";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import PageMeta from "./PageMeta";
import ProductCard from "./ProductCard";

const PAGE_SIZE = 12;
const EMPTY_CATALOG = [];

const normalizeCategoryKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const categoryLookup = siteConfig.categories.reduce((lookup, category) => {
  [category.id, category.name, category.shortName].forEach((value) => {
    lookup.set(normalizeCategoryKey(value), category);
  });
  return lookup;
}, new Map());

const getSelectedCategories = (params) => {
  const requested = params
    .getAll("category")
    .flatMap((value) => value.split(","))
    .map((value) => categoryLookup.get(normalizeCategoryKey(value)))
    .filter(Boolean);

  return siteConfig.categories.filter((category) =>
    requested.some((item) => item.id === category.id)
  );
};

const collectionLabels = {
  "new-arrivals": "New arrivals",
  "best-sellers": "Best sellers",
  bestsellers: "Best sellers",
};

const sortOptions = new Set([
  "featured",
  "newest",
  "price-asc",
  "price-desc",
  "name",
]);

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const urlSearch = searchParams.get("q") || searchParams.get("search") || "";
  const [searchDraft, setSearchDraft] = useState(urlSearch);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersAreDrawer, setFiltersAreDrawer] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(max-width: 880px)").matches
      : false
  );
  const [isLoading, setIsLoading] = useState(true);
  const resultsRef = useRef(null);
  const filterPanelRef = useRef(null);
  const filterToggleRef = useRef(null);

  const catalogIsValid =
    Array.isArray(productData) &&
    productData.every(
      (product) =>
        product &&
        product.id !== undefined &&
        product.name &&
        product.category &&
        Number.isFinite(Number(product.price)) &&
        Array.isArray(product.images)
    );
  const catalog = catalogIsValid ? productData : EMPTY_CATALOG;
  const catalogError = catalogIsValid
    ? ""
    : "We could not load the pantry right now. Please refresh and try again.";

  const catalogPriceCeiling = Math.max(
    1,
    Math.ceil(Math.max(...catalog.map((product) => Number(product.price)), 1))
  );
  const maxPriceParam = searchParams.get("max");
  const requestedMaxPrice = maxPriceParam === null ? Number.NaN : Number(maxPriceParam);
  const maxPrice = Number.isFinite(requestedMaxPrice) && requestedMaxPrice >= 0
    ? Math.min(requestedMaxPrice, catalogPriceCeiling)
    : catalogPriceCeiling;
  const selectedCategories = getSelectedCategories(searchParams);
  const selectedCategoryKey = selectedCategories
    .map((category) => category.id)
    .join(",");
  const stockOnly = searchParams.get("stock") === "1";
  const requestedSort = searchParams.get("sort") || "featured";
  const sort = sortOptions.has(requestedSort) ? requestedSort : "featured";
  const collection = searchParams.get("collection") || "";

  useEffect(() => {
    setSearchDraft(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 880px)");
    const updateMode = (event) => {
      setFiltersAreDrawer(event.matches);
      if (!event.matches) setFiltersOpen(false);
    };

    setFiltersAreDrawer(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", updateMode);
    return () => mediaQuery.removeEventListener?.("change", updateMode);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const timer = window.setTimeout(() => setIsLoading(false), 180);
    return () => window.clearTimeout(timer);
  }, [queryString]);

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const handleDrawerKeyDown = (event) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
        filterToggleRef.current?.focus();
        return;
      }

      if (event.key === "Tab") {
        const focusableElements = Array.from(
          filterPanelRef.current?.querySelectorAll(
            'button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href]'
          ) || []
        );
        if (!focusableElements.length) return;
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    filterPanelRef.current
      ?.querySelector("button, input, select, a[href]")
      ?.focus();
    window.addEventListener("keydown", handleDrawerKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleDrawerKeyDown);
    };
  }, [filtersOpen]);

  const closeFilters = () => {
    setFiltersOpen(false);
    window.setTimeout(() => filterToggleRef.current?.focus(), 0);
  };

  const updateParams = (updates, options = {}) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined || value === false) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });

    if (!Object.prototype.hasOwnProperty.call(updates, "page")) {
      next.delete("page");
    }
    setSearchParams(next, { replace: Boolean(options.replace) });
  };

  const filteredProducts = useMemo(() => {
    const phrase = urlSearch.trim().toLowerCase();
    const categoryNames = new Set(
      selectedCategoryKey
        .split(",")
        .filter(Boolean)
        .map((id) => siteConfig.categories.find((category) => category.id === id)?.name)
        .filter(Boolean)
    );

    const matches = catalog.filter((product) => {
      const searchableText = [
        product.name,
        product.category,
        product.description,
        ...(Array.isArray(product.tags) ? product.tags : []),
      ]
        .join(" ")
        .toLowerCase();
      const matchesSearch = !phrase || searchableText.includes(phrase);
      const matchesCategory =
        categoryNames.size === 0 || categoryNames.has(product.category);
      const matchesPrice = Number(product.price) <= maxPrice;
      const matchesStock = !stockOnly || Number(product.stock) > 0;
      const matchesCollection =
        !collection ||
        ((collection === "new-arrivals" || collection === "new") && product.newArrival) ||
        ((collection === "best-sellers" || collection === "bestsellers") &&
          product.bestseller);

      return (
        matchesSearch &&
        matchesCategory &&
        matchesPrice &&
        matchesStock &&
        matchesCollection
      );
    });

    return [...matches].sort((left, right) => {
      if (sort === "price-asc") return left.price - right.price;
      if (sort === "price-desc") return right.price - left.price;
      if (sort === "name") return left.name.localeCompare(right.name);
      if (sort === "newest") {
        return Number(right.newArrival) - Number(left.newArrival) || right.id - left.id;
      }

      const rightScore = Number(right.featured) * 2 + Number(right.bestseller);
      const leftScore = Number(left.featured) * 2 + Number(left.bestseller);
      return rightScore - leftScore || left.id - right.id;
    });
  }, [catalog, collection, maxPrice, selectedCategoryKey, sort, stockOnly, urlSearch]);

  const requestedPage = Math.max(1, Number.parseInt(searchParams.get("page"), 10) || 1);
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const currentPage = Math.min(requestedPage, totalPages);
  const pageProducts = filteredProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const activeFilterCount =
    selectedCategories.length +
    Number(maxPrice < catalogPriceCeiling) +
    Number(stockOnly) +
    Number(Boolean(urlSearch)) +
    Number(Boolean(collection));
  const hasActiveFilters = activeFilterCount > 0;
  const collectionLabel = collectionLabels[collection] || collection.replace(/-/g, " ");

  const submitSearch = (event) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    next.delete("search");
    next.delete("page");
    const phrase = searchDraft.trim();
    if (phrase) next.set("q", phrase);
    else next.delete("q");
    setSearchParams(next);
  };

  const toggleCategory = (categoryId) => {
    const selected = new Set(selectedCategories.map((category) => category.id));
    if (selected.has(categoryId)) selected.delete(categoryId);
    else selected.add(categoryId);

    const orderedIds = siteConfig.categories
      .filter((category) => selected.has(category.id))
      .map((category) => category.id);
    updateParams({ category: orderedIds.join(",") });
  };

  const resetFilters = () => {
    setSearchDraft("");
    setFiltersOpen(false);
    setSearchParams(new URLSearchParams());
  };

  const changePage = (page) => {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) next.delete("page");
    else next.set("page", String(page));
    setSearchParams(next);

    window.setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  return (
    <div className="shop main">
      <PageMeta
        title="Shop the pantry"
        description={`Browse pasta, olive oil, tomatoes, sweets, and more from ${siteConfig.brandName}.`}
      />

      <main className="shop__main">
        <header className="shop__masthead">
          <nav className="shop__breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Shop</span>
          </nav>
          <div className="shop__title-row">
            <h1>The Italian pantry</h1>
            <p>
              Full-flavored staples, joyful sweets, and thoughtful ingredients
              made to earn a permanent place on your shelf.
            </p>
          </div>
          <form className="shop__search" role="search" onSubmit={submitSearch}>
            <label htmlFor="catalog-search">Search the pantry</label>
            <div className="shop__search-control">
              <FiSearch aria-hidden="true" />
              <input
                id="catalog-search"
                type="search"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                placeholder="Try pasta, Sicily, lemon…"
              />
              <button type="submit">Search</button>
            </div>
          </form>
        </header>

        <div className="shop__toolbar">
          <button
            className="shop__filter-toggle"
            type="button"
            ref={filterToggleRef}
            aria-expanded={filtersOpen}
            aria-controls="shop-filters"
            onClick={() => setFiltersOpen(true)}
          >
            <FiSliders aria-hidden="true" />
            Filters {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
          </button>
          <p className="shop__result-count" aria-live="polite">
            <strong>{filteredProducts.length}</strong>{" "}
            {filteredProducts.length === 1 ? "product" : "products"}
          </p>
          <label className="shop__sort" htmlFor="catalog-sort">
            <span>Sort by</span>
            <select
              id="catalog-sort"
              value={sort}
              onChange={(event) => updateParams({ sort: event.target.value })}
            >
              <option value="featured">Featured</option>
              <option value="newest">Newest first</option>
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
              <option value="name">Name: A–Z</option>
            </select>
          </label>
        </div>

        {catalogError ? (
          <section className="shop__state shop__state--error" role="alert">
            <p className="home-kicker">Something went wrong</p>
            <h2>The shelves are unavailable.</h2>
            <p>{catalogError}</p>
          </section>
        ) : (
          <div className="shop__catalog">
            <button
              className={`shop__filters-backdrop ${filtersOpen ? "is-open" : ""}`}
              type="button"
              aria-label="Close filters"
              tabIndex={filtersOpen ? 0 : -1}
              onClick={closeFilters}
            />

            <aside
              className={`shop__filters ${filtersOpen ? "is-open" : ""}`}
              id="shop-filters"
              aria-label="Catalog filters"
              aria-hidden={filtersAreDrawer && !filtersOpen ? true : undefined}
              ref={filterPanelRef}
              inert={filtersAreDrawer && !filtersOpen ? "" : undefined}
            >
              <div className="shop__filters-heading">
                <h2>Filter the pantry</h2>
                <button
                  className="shop__filter-close"
                  type="button"
                  aria-label="Close filters"
                  onClick={closeFilters}
                >
                  <FiX aria-hidden="true" />
                </button>
              </div>

              {collection && (
                <div className="shop__applied-filter">
                  <span>Collection</span>
                  <button
                    type="button"
                    onClick={() => updateParams({ collection: "" })}
                    aria-label={`Remove ${collectionLabel} collection filter`}
                  >
                    {collectionLabel} <FiX aria-hidden="true" />
                  </button>
                </div>
              )}

              <fieldset className="shop__filter-group">
                <legend>Category</legend>
                <div className="shop__checks">
                  {siteConfig.categories.map((category) => {
                    const count = catalog.filter(
                      (product) => product.category === category.name
                    ).length;
                    const checked = selectedCategories.some(
                      (selected) => selected.id === category.id
                    );

                    return (
                      <label key={category.id}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCategory(category.id)}
                        />
                        <span className="shop__custom-check" aria-hidden="true" />
                        <span>{category.shortName}</span>
                        <small>{count}</small>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <fieldset className="shop__filter-group shop__price-filter">
                <legend>Maximum price</legend>
                <div className="shop__price-readout">
                  <span>$0</span>
                  <output htmlFor="max-price">Up to ${maxPrice}</output>
                </div>
                <input
                  id="max-price"
                  type="range"
                  min="0"
                  max={catalogPriceCeiling}
                  step="1"
                  value={maxPrice}
                  aria-valuetext={`Up to ${maxPrice} dollars`}
                  onChange={(event) =>
                    updateParams(
                      {
                        max:
                          Number(event.target.value) >= catalogPriceCeiling
                            ? ""
                            : event.target.value,
                      },
                      { replace: true }
                    )
                  }
                />
              </fieldset>

              <fieldset className="shop__filter-group">
                <legend>Availability</legend>
                <div className="shop__checks">
                  <label>
                    <input
                      type="checkbox"
                      checked={stockOnly}
                      onChange={(event) =>
                        updateParams({ stock: event.target.checked ? "1" : "" })
                      }
                    />
                    <span className="shop__custom-check" aria-hidden="true" />
                    <span>In stock only</span>
                  </label>
                </div>
              </fieldset>

              <button
                className="shop__reset"
                type="button"
                onClick={resetFilters}
                disabled={!hasActiveFilters && sort === "featured"}
              >
                Reset all filters
              </button>
            </aside>

            <section className="shop__results" ref={resultsRef} aria-label="Products">
              {isLoading ? (
                <div className="shop__grid" aria-busy="true" aria-label="Loading products">
                  {Array.from({ length: PAGE_SIZE }, (_, index) => (
                    <div className="shop-skeleton" key={index} aria-hidden="true">
                      <span />
                      <i />
                      <i />
                    </div>
                  ))}
                </div>
              ) : pageProducts.length > 0 ? (
                <>
                  <div className="shop__grid">
                    {pageProducts.map((product, index) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        tone="paper"
                        priority={currentPage === 1 && index < 4}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <nav className="shop__pagination" aria-label="Product pages">
                      <button
                        type="button"
                        onClick={() => changePage(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        <FiChevronLeft aria-hidden="true" />
                      </button>
                      {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                        (page) => (
                          <button
                            type="button"
                            className={page === currentPage ? "is-current" : ""}
                            aria-current={page === currentPage ? "page" : undefined}
                            onClick={() => changePage(page)}
                            key={page}
                          >
                            {page}
                          </button>
                        )
                      )}
                      <button
                        type="button"
                        onClick={() => changePage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                      >
                        <FiChevronRight aria-hidden="true" />
                      </button>
                    </nav>
                  )}
                </>
              ) : (
                <div className="shop__state shop__state--empty">
                  <p className="home-kicker">No matches yet</p>
                  <h2>Try a wider pantry search.</h2>
                  <p>
                    Clear a filter or try another ingredient to see more of the
                    collection.
                  </p>
                  <button type="button" onClick={resetFilters}>
                    Reset filters
                  </button>
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

export default Shop;
